import { useEffect, useRef, useState } from 'react';
import { Text } from '@/components/ui/Text';
import { Button } from '@/components/ui/Button';
import { postVerifyEmail, type VerifyOutcome } from '@/lib/auth/verify-api';

type Phase =
  | { tag: 'loading' }
  | { tag: 'missing' }
  | { tag: 'done'; outcome: VerifyOutcome };

function getToken(): string | null {
  return new URLSearchParams(window.location.search).get('token');
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-bg text-text flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center space-y-4">
        {children}
      </div>
    </div>
  );
}

export function VerifyApp() {
  const [phase, setPhase] = useState<Phase>(() =>
    getToken() ? { tag: 'loading' } : { tag: 'missing' }
  );

  // consumedRef persists across StrictMode double-fire so the single-use token
  // is POSTed exactly once even in development (same guard as BulkImportWizard).
  const consumedRef = useRef(false);

  useEffect(() => {
    const token = getToken();
    if (!token || consumedRef.current) return;
    consumedRef.current = true;

    postVerifyEmail(token).then((outcome) => setPhase({ tag: 'done', outcome }));
  }, []);

  if (phase.tag === 'loading') {
    return (
      <Shell>
        <Text variant="headline-sm" as="h1">Verifying your email…</Text>
        <Text variant="body-md" color="muted">Just a moment.</Text>
      </Shell>
    );
  }

  if (phase.tag === 'missing') {
    return (
      <Shell>
        <Text variant="headline-sm" color="danger" as="h1">Link invalid</Text>
        <Text variant="body-md" color="muted">
          No verification token was found. Use the link from your verification email, or request a new one.
        </Text>
        <Button variant="outline" onClick={() => { window.location.href = '/' }}>
          Go to sign in
        </Button>
      </Shell>
    );
  }

  const { outcome } = phase;

  if (outcome.ok) {
    return (
      <Shell>
        <Text variant="headline-sm" color="success" as="h1" data-testid="verify-success-heading">
          Email verified
        </Text>
        <Text variant="body-md" color="muted">
          Your email address has been confirmed. You can now sign in.
        </Text>
        <Button onClick={() => { window.location.href = '/' }}>
          Sign in
        </Button>
      </Shell>
    );
  }

  if (outcome.reason === 'network_error') {
    return (
      <Shell>
        <Text variant="headline-sm" color="danger" as="h1">Could not connect</Text>
        <Text variant="body-md" color="muted">
          The verification service did not respond. Check your connection and try the link again.
        </Text>
        <Button variant="outline" onClick={() => { window.location.reload() }}>
          Try again
        </Button>
      </Shell>
    );
  }

  // invalid_or_expired — covers: already consumed (second click), expired, malformed.
  // The API returns the same error for all three; we cannot distinguish them.
  return (
    <Shell>
      <Text variant="headline-sm" color="warning" as="h1" data-testid="verify-invalid-heading">
        Link already used or expired
      </Text>
      <Text variant="body-md" color="muted">
        This verification link has already been used or is no longer valid.
        If your email is already verified, sign in directly.
      </Text>
      <Button onClick={() => { window.location.href = '/' }}>
        Sign in
      </Button>
    </Shell>
  );
}
